import { ApiEndpoints } from "@shared/api/api-endpoints";
import { ixApiClient } from "@shared/api/clients/apiClientFactory";

const getAvailableFlows = async (connectionId: string): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/available-flows`,
    );
    const response = await ixApiClient.get(url);
    return response.data.result;
  } catch (error) {
    throw error;
  }
};

const createConnectionFlow = async (
  connectionId: string,
  payload: any,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/flows`,
    );
    const response = await ixApiClient.post(url, payload);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

const getConnectionFlows = async (connectionId: string): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/flows`,
    );
    const response = await ixApiClient.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const updateConnectionFlow = async (
  connectionId: string,
  flowId: string,
  payload: { is_active: boolean },
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/flows/${flowId}`,
    );
    const response = await ixApiClient.patch(url, payload);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

const deleteConnectionFlow = async (
  connectionId: string,
  flowId: string,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/flows/${flowId}`,
    );
    const response = await ixApiClient.delete(url);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

const activateConnection = async (connectionId: string): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/activate`,
    );
    const response = await ixApiClient.post(url, {});
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

const getConnectionHealthTimeline = async (
  connectionId: string,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/health-timeline`,
    );
    const response = await ixApiClient.get(url);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

const getConnectionStats = async (
  connectionId: string,
  period: string = "24h",
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ix",
      `connections/${connectionId}/stats`,
      (query) => {
        query.push("period", period);
        return query;
      },
    );
    const response = await ixApiClient.get(url);
    return response.data.result;
  } catch (error) {
    throw error;
  }
};

const getConnectionFlowsSummary = async (
  connectionId: string,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/flows-summary`,
    );
    const response = await ixApiClient.get(url);
    return response.data.result;
  } catch (error) {
    throw error;
  }
};

const getHealthTimeline = async (connectionId: string): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrl(
      "ix",
      `connections/${connectionId}/health-timeline`,
    );
    const response = await ixApiClient.get(url);
    return response.data.result;
  } catch (error) {
    throw error;
  }
};

export interface SyncLogsFilters {
  period?: string;
  status?: string;
  flow?: string;
  search_data?: string;
  page?: number;
  limit?: number;
}

const getSyncLogs = async (
  connectionId: string,
  filters: SyncLogsFilters,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ix",
      `connections/${connectionId}/sync-logs`,
      (query) => {
        if (filters.period) query.push("period", filters.period);
        if (filters.status) query.push("status", filters.status);
        if (filters.flow && filters.flow !== "all")
          query.push("flow", filters.flow);
        if (filters.search_data) query.push("search_data", filters.search_data);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      },
    );
    const response = await ixApiClient.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface ConnectionErrorsFilters {
  page?: number;
  limit?: number;
}

const getConnectionErrors = async (
  connectionId: string,
  filters: ConnectionErrorsFilters,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ix",
      `connections/${connectionId}/connection-errors`,
      (query) => {
        query.push("period", "7d");
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      },
    );
    const response = await ixApiClient.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getMonitoring = async (
  connectionId: string,
  period: string,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ix",
      `connections/${connectionId}/monitoring`,
      (query) => {
        query.push("period", period);
        return query;
      },
    );
    const response = await ixApiClient.get(url);
    return response.data.result;
  } catch (error) {
    throw error;
  }
};

export interface VersionsFilters {
  page?: number;
  limit?: number;
}

const getVersions = async (
  connectionId: string,
  filters: VersionsFilters,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ix",
      `connections/${connectionId}/versions`,
      (query) => {
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      },
    );
    const response = await ixApiClient.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};
const getConnectionDetails = async (
  connectionId: string,
  remote_utility_id: string,
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ix",
      `connections/${connectionId}`,
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        return query;
      },
    );
    const response = await ixApiClient.get(url);
    return response.data.result;
  } catch (error) {
    throw error;
  }
};

export const ixApi = {
  getAvailableFlows,
  createConnectionFlow,
  getConnectionFlows,
  updateConnectionFlow,
  deleteConnectionFlow,
  activateConnection,
  getConnectionHealthTimeline,
  getConnectionStats,
  getConnectionFlowsSummary,
  getHealthTimeline,
  getSyncLogs,
  getConnectionErrors,
  getMonitoring,
  getVersions,
  getConnectionDetails,
};
